import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core'

import { GbColorService } from 'libs/shared/utils/services/src/lib/gb-color-service/gb-color.service'
import { ItemTypeEnum, ProjectorItemsEntity } from 'libs/gb-dashboard/item/data-access/src/lib/+state/items.models'
import { ItemTableWidget } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { TranslateModule } from '@ngx-translate/core'
import { GbDashboardSharedUtilsModule } from 'libs/gb-dashboard/shared/utils/src/lib/gb-dashboard-shared-utils.module'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { GbCurrencyPipe } from 'libs/shared/utils/pipes/src/lib/gb-currency-pipe/gb-currency.pipe'

@Component({
  selector: 'gb-presenter-widget-item-table-card-compact',
  imports: [TranslateModule, GbDashboardSharedUiModule, GbDashboardSharedUtilsModule, GbCurrencyPipe],
  templateUrl: './gb-presenter-widget-item-table-card-compact.component.html',
  styleUrl: './gb-presenter-widget-item-table-card-compact.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetItemTableCardCompactComponent {
  private gbColorService = inject(GbColorService)

  constructor(private colorResolver: GbPresenterColorResolverService) {}

  @Input({ required: true }) item!: ProjectorItemsEntity
  @Input({ required: true }) itemTableWidget!: ItemTableWidget
  @Input({ required: true }) itemTypeTranslateKey!: string

  ItemTypeEnum = ItemTypeEnum

  get resolvedTableBackgroundColor(): string {
    return this.colorResolver.resolve(this.itemTableWidget.table_background_color) || ''
  }

  get resolvedTablePrimaryColor(): string {
    return this.colorResolver.resolve(this.itemTableWidget.table_primary_color) || ''
  }

  get backgroundContrastColor(): string {
    return this.gbColorService.getContrast(this.resolvedTableBackgroundColor)
  }

  get primaryContrastColor(): string {
    return this.gbColorService.getContrast(this.resolvedTablePrimaryColor)
  }

  get itemImage() {
    return this.item?.images?.[0]?.image
  }

  get imageCover(): boolean {
    return this.item?.image_cover ?? !this.itemTableWidget?.image_contained
  }

  get isBiddingType(): boolean {
    return [ItemTypeEnum.Bidding, ItemTypeEnum.Live, ItemTypeEnum.Sealed].includes(this.item?.item_type)
  }

  get hasWinningBids(): boolean {
    return !!this.item?.bidding_item?.winning_bids?.length
  }

  get biddingSoldOut(): boolean {
    const biddingItem = this.item.bidding_item
    return !biddingItem?.units_remaining
  }

  get highestBidderName(): string {
    return this.item?.bidding_item?.winning_bids?.[0]?.profile_full_name ?? ''
  }

  get highestBidAmount(): number | null {
    return this.item?.bidding_item?.winning_bids?.[0]?.amount ?? null
  }

  get ticketsRemaining(): number | null {
    if (this.item?.item_type === ItemTypeEnum.Raffle) {
      return this.item?.raffle_item?.tickets_remaining ?? null
    }
    if (this.item?.item_type === ItemTypeEnum.Purchase) {
      return this.item?.purchase_item?.units_remaining ?? null
    }
    return null
  }

  get donationRaised(): number | null {
    return this.item?.donation_item?.total_collected ?? null
  }

  get fontSize(): number {
    return (this.itemTableWidget?.font_size ?? 0) / 8
  }
}
